import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspaceContext } from '../../../../../../../../providers/WorkspaceProvider/Workspace.context';
import { useToastContext } from '../../../../../../../../providers/ToastProvider/Toast.hook';
import { eAlertType } from '../../../../../../../../providers/ToastProvider/Toast.enum';
import { autoRespondService } from '../../../../../../../../services/AI/autoRespond.service';
import { useChatContext } from '../../../../../../Chat.hook';
import { useBoardSlice } from 'src/pages/Pipe/PipeBoard.context';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { smartLeadbotConfigService } from 'src/pages/Settings/components/SmartLeadbotConfig/SmartLeadbotConfig.service';
import type { tSmartLeadbotConfig } from 'src/pages/Settings/components/SmartLeadbotConfig/SmartLeadbotConfig.types';
import { LEADAGENT_CHAT_EVENTS } from 'src/hooks/analytics/chat/leadagentChatAnalytics.constants';
import type {
  tHandoffReasonDismissReason,
  tHandoffReasonEntryPoint,
} from './components/HandoffReasonModal/handoffReasonOptions.types';
import { useHandoffOptions } from './components/HandoffReasonModal/useHandoffOptions.hook';
import type {
  tHandoffReasonModalProps,
  tHandoffReasonModalResult,
} from './components/HandoffReasonModal/HandoffReasonModal.types';
import {
  getOrInitSlbActivatedAt,
  recordReasonAsked,
  shouldAskForReason,
  workspaceSlbAgeDaysFrom,
} from './utils/handoffReasonFrequency.util';
import { evaluateSlbToggleGate } from './utils/slbToggleGate.util';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook';

// Emitted when a human takes a chat the bot was attending. Under V2 this
// fires immediately after the PATCH succeeds (post-success flow). The
// reason picker (HandoffReasonModal) opens separately as a post-success
// survey and emits its own dedicated front.leadagent.chat.handoff_reason.*
// events; correlate by chat_id + timestamp on the dashboard.
const HANDOFF_TAKEN_EVENT = 'front.leadagent.chat.handoff.taken';

export type tDisabledReasonInfo = {
  message: string;
  actionLabel: string | null;
  actionLink: string | null;
};

type tAutoRespondToggleResult = {
  enabled: boolean;
  isSaving: boolean;
  // V2 (RET-1069): handleToggle requires the entryPoint from the
  // consumer so the right schema + cooldown apply.
  //   - MessageBar "Tomar control" pill → 'tomar_control' (4h cooldown,
  //     optional flat survey)
  //   - AIResponsePreview switch → 'apagar_bot_chat' (30min cooldown,
  //     mandatory nested survey)
  handleToggle: (entryPoint: tHandoffReasonEntryPoint) => void;
  disabledReason: tDisabledReasonInfo | null;
  deactivationReason: string;
  // The open chat's session read has settled (answered or failed), so
  // `deactivationReason` is final for it. False while it loads and right after
  // a chat change.
  isDeactivationReasonLoaded: boolean;
  handoffReasonModalProps: tHandoffReasonModalProps;
};

type tPendingHandoffContext = {
  chatId: string;
  leadId: string | null;
  channelType: string;
  workspaceSlbAgeDays: number;
  entryPoint: tHandoffReasonEntryPoint;
};

// Fallback when the modal needs a schema but no pendingContext is set
// (shouldn't normally happen; defensive). Mandatory nested apagar_bot_chat
// is the safer choice — it surfaces the richer survey if the consumer
// somehow opens the modal without specifying an entryPoint.
const FALLBACK_PER_CHAT_ENTRY_POINT: tHandoffReasonEntryPoint =
  'apagar_bot_chat';

export default function useAutoRespondToggle(): tAutoRespondToggleResult {
  const { t } = useTranslation('chat/chat');
  const { currentWorkspace } = useWorkspaceContext();
  const { addAlert } = useToastContext();
  const {
    selectedChat,
    setSelectedChat,
    autoRespondEnabled: enabled,
    setAutoRespondEnabled: setEnabled,
  } = useChatContext();
  const { updateLeadAiRespondState } = useBoardSlice();
  const {
    slbSessionStateChangedEvent,
    slbEscalatedEvent,
    slbSessionExpiredEvent,
    leadUpdatedEvent,
  } = useSocketsContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { trackEvent } = useAnalytics();
  // V2 (RET-1069 Chunk C): runtime-tunable option schemas from PostHog
  // feature flag `lead_agent_handoff_options`. Falls back to baked-in
  // defaults if the flag isn't set or PostHog hasn't loaded yet.
  const handoffSchemas = useHandoffOptions();
  const { canSendOutboundMessages } = useACLContext();

  const [isSaving, setIsSaving] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState('');
  // Id of the chat whose session read settled: a late answer for the previous
  // chat cannot mark the current one as loaded.
  const [deactivationReasonLoadedChatId, setDeactivationReasonLoadedChatId] =
    useState<string | null>(null);
  const [slbConfig, setSlbConfig] = useState<tSmartLeadbotConfig | null>(null);
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [pendingHandoffContext, setPendingHandoffContext] =
    useState<tPendingHandoffContext | null>(null);
  // AGI-105 — freshest funnel/stage for the SELECTED lead, fed by lead.updated.
  // The gate's default position input (selectedLeadForChatModal) is captured at
  // modal-open and never refreshed, so a background move (e.g. the LeadBot →
  // Lead Agent handoff under coexistence) left the yellow
  // "stage_not_bot_enabled" banner stale until the chat was reopened (observed
  // live on Efrain WAB). Same pattern as
  // docs/solutions/logic-errors/lazy-loaded-state-stale-on-entity-change.md.
  const [leadPositionOverride, setLeadPositionOverride] = useState<{
    funnelId?: string;
    stageId?: string;
  } | null>(null);

  // Auto-dismiss the modal if the operator switches chats mid-decision. The
  // pendingHandoffContext targets a specific chat captured at modal-open time;
  // applying the toggle to a different selectedChat would corrupt that chat's
  // state. We do NOT emit a DISMISSED event for chat-change dismissal — that
  // event represents explicit user action (clicked X), not system-initiated.
  useEffect(() => {
    setIsReasonModalOpen(false);
    setPendingHandoffContext(null);
  }, [selectedChat?.id]);

  // The override belongs to ONE lead; switching leads must fall back to the
  // modal-context position until a fresh event arrives for the new lead.
  useEffect(() => {
    setLeadPositionOverride(null);
  }, [selectedLeadForChatModal?.id]);

  // Track background moves of the selected lead (funnel/stage change while the
  // chat is open) so the gate below re-evaluates against the REAL position.
  leadUpdatedEvent.useSubscription((event) => {
    const updated = event.response;
    if (!updated?.uuid || updated.uuid !== selectedLeadForChatModal?.id) return;
    setLeadPositionOverride({
      funnelId: updated.funnel?.uuid ?? undefined,
      stageId: updated.stage?.uuid ?? undefined,
    });
  });

  // Fetch SLB config once per workspace change. On failure, allow toggle (optimistic — backend catches).
  useEffect(() => {
    if (!currentWorkspace?.id) return;
    smartLeadbotConfigService
      .getConfig(currentWorkspace.id)
      .then(setSlbConfig)
      .catch(() => setSlbConfig(null));
  }, [currentWorkspace?.id]);

  // Compute disabled reason from local data. Gate logic lives in the pure
  // evaluateSlbToggleGate util (unit-tested; mirrors the backend gates); here we only
  // map its machine-readable code to the translated message/label. Gate 4 (channel)
  // is validated by the backend only. Note Gate 3 (human stage) blocks even when the
  // bot is currently ON — the backend will deactivate via the stage-change event, and
  // showing the toggle disabled gives immediate visual feedback while it catches up.
  const disabledReason = useMemo((): tDisabledReasonInfo | null => {
    if (canSendOutboundMessages === false) {
      return {
        message: t('smartLeadbot.planPauseDisabled'),
        actionLabel: null,
        actionLink: null,
      };
    }
    const gate = evaluateSlbToggleGate({
      config: slbConfig,
      // AGI-105: prefer the live position from lead.updated over the snapshot
      // captured when the chat modal opened (see leadPositionOverride above).
      leadFunnelId:
        leadPositionOverride?.funnelId ??
        selectedLeadForChatModal?.funnel?.uuid,
      leadStageId:
        leadPositionOverride?.stageId ?? selectedLeadForChatModal?.stage?.id,
      enabled,
    });
    if (!gate) return null;
    return {
      message: t(`smartLeadbot.disabledReasons.${gate.code}`),
      actionLabel: t(`smartLeadbot.disabledReasons.${gate.code}Action`),
      actionLink: gate.actionLink,
    };
  }, [
    canSendOutboundMessages,
    enabled,
    slbConfig,
    leadPositionOverride,
    selectedLeadForChatModal?.funnel?.uuid,
    selectedLeadForChatModal?.stage?.id,
    t,
  ]);

  // React to backend-pushed state changes (stage change, escalation, manual toggle from another tab).
  // This is the single authoritative update path — covers all triggers without polling.
  slbSessionStateChangedEvent.useSubscription((event) => {
    if (event.chat_id !== selectedChat?.id) return;
    setEnabled(event.enabled);
    setSelectedChat((prev) =>
      prev ? { ...prev, aiRespondEnabled: event.enabled } : null,
    );
    if (event.lead_id) updateLeadAiRespondState(event.lead_id, event.enabled);
  });

  // Escalation and expiry also deactivate the SLB for this chat.
  slbEscalatedEvent.useSubscription((event) => {
    if (event.chat_id !== selectedChat?.id) return;
    setEnabled(false);
    setSelectedChat((prev) =>
      prev ? { ...prev, aiRespondEnabled: false } : null,
    );
  });

  slbSessionExpiredEvent.useSubscription((event) => {
    if (event.chat_id !== selectedChat?.id) return;
    setEnabled(false);
    setSelectedChat((prev) =>
      prev ? { ...prev, aiRespondEnabled: false } : null,
    );
  });

  // When the selected chat changes, always fetch fresh state from the backend.
  // We intentionally skip the selectedChat.aiRespondEnabled cache here because it can
  // be stale: if the bot was deactivated in a previous session and a new session was
  // created via chat.created while the modal was closed, the cached false would persist.
  useEffect(() => {
    setEnabled(false);
    setDeactivationReason('');
    setDeactivationReasonLoadedChatId(null);
    if (!currentWorkspace?.id || !selectedChat?.id) return;
    const requestedChatId = selectedChat.id;
    // A late answer for the previous chat must not write its state onto the
    // chat that is open now.
    let cancelled = false;

    autoRespondService
      .getState(currentWorkspace.id, selectedChat.id)
      .then((result) => {
        if (cancelled) return;
        setEnabled(result.enabled);
        setDeactivationReason(result.deactivationReason ?? '');
        setDeactivationReasonLoadedChatId(requestedChatId);
        setSelectedChat((prev) =>
          prev ? { ...prev, aiRespondEnabled: result.enabled } : null,
        );
        // NOTE: Do NOT push per-chat state to the pipe kanban card here.
        // The kanban card represents the lead's overall SLB state (across all chats),
        // not a single chat's state. The bulk fetch and WebSocket events handle kanban updates.
      })
      .catch(() => {
        if (cancelled) return;
        setEnabled(false);
        setDeactivationReasonLoadedChatId(requestedChatId);
      });

    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id, selectedChat?.id]);

  // performToggle does the API call, optimistic update, and error handling.
  // V2 (RET-1069): no longer takes a `reason` argument — the modal is now
  // a post-success survey and reason fields are emitted only as PostHog
  // analytics events from the modal handlers, not embedded in this PATCH.
  // Returns true on success, false on failure, so handleToggle can decide
  // whether to open the post-success modal (open only when PATCH succeeded).
  const performToggle = useCallback(
    async (next: boolean): Promise<boolean> => {
      if (!currentWorkspace?.id || !selectedChat?.id) return false;

      // Re-derive isHandoff from current enabled — a WebSocket event could
      // have flipped state during the modal display. If enabled is already
      // false, no handoff happened.
      const isHandoff = enabled && !next;
      const chatId = selectedChat.id;
      // RET-870 — push the optimistic update to every SLB surface at once so the
      // MessageBar button, the chat-context aiRespondEnabled flag, and the kanban
      // "Lead Agent" chip all transition together. On failure every surface
      // rolls back in lockstep.
      setEnabled(next);
      setSelectedChat((prev) =>
        prev ? { ...prev, aiRespondEnabled: next } : null,
      );
      if (selectedChat.leadId)
        updateLeadAiRespondState(selectedChat.leadId, next);

      setIsSaving(true);
      try {
        await autoRespondService.toggle(selectedChat.id, {
          workspaceId: currentWorkspace.id,
          enabled: next,
          channel_type: selectedChat.integration,
          lead_id: selectedChat.leadId,
        });
        if (isHandoff) {
          // bot_messages_before_handoff stays null until the FE has a stable way
          // to identify bot-authored chat events — `tChatEvent.createdBy` is a
          // raw UUID without a documented "is bot" convention exposed to the
          // client. Backend can backfill this from chatbot-service if needed.
          trackEvent(HANDOFF_TAKEN_EVENT, {
            chat_id: chatId,
            bot_messages_before_handoff: null,
          });
        }
        return true;
      } catch (error) {
        setEnabled(!next);
        setSelectedChat((prev) =>
          prev ? { ...prev, aiRespondEnabled: !next } : null,
        );
        if (selectedChat.leadId)
          updateLeadAiRespondState(selectedChat.leadId, !next);
        const errorResponse = (
          error as {
            response?: {
              status?: number;
              data?: { code?: string; message?: string };
            };
          }
        )?.response;
        const status = errorResponse?.status;
        const serverCode = errorResponse?.data?.code;
        const serverMessage = errorResponse?.data?.message;

        if (status === 422) {
          const codeToI18nKey: Record<string, string> = {
            config_not_active: 'smartLeadbot.disabledReasons.configNotActive',
            funnel_mismatch: 'smartLeadbot.disabledReasons.funnelMismatch',
            stage_not_bot_enabled:
              'smartLeadbot.disabledReasons.stageNotBotEnabled',
            channel_not_integrated:
              'smartLeadbot.disabledReasons.channelNotIntegrated',
          };
          const i18nKey = serverCode ? codeToI18nKey[serverCode] : undefined;
          addAlert({
            type: eAlertType.ERROR,
            message: i18nKey
              ? t(i18nKey)
              : (serverMessage ??
                t('smartLeadbot.disabledReasons.configNotActive')),
          });
        } else {
          addAlert({
            type: eAlertType.ERROR,
            message:
              status === 404
                ? 'Esta conversación no tiene una sesión activa de Lead Agent. El bot solo se activa en chats nuevos.'
                : 'No se pudo actualizar el estado del Lead Agent.',
          });
        }
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [
      currentWorkspace?.id,
      selectedChat?.id,
      selectedChat?.integration,
      selectedChat?.leadId,
      enabled,
      trackEvent,
      addAlert,
      t,
      setEnabled,
      setSelectedChat,
      updateLeadAiRespondState,
    ],
  );

  // handleToggle — V2 post-success flow.
  //   1. Fire the PATCH immediately (the operator's primary action is
  //      "turn the bot off" — don't gate that on a survey).
  //   2. If the PATCH failed, the optimistic update was rolled back and
  //      an error alert fired inside performToggle. No modal.
  //   3. If the PATCH succeeded and this was a handoff (going off), check
  //      the per-entry-point cooldown. If the gate says ask, open the
  //      modal as a post-success survey. The deactivation is committed
  //      either way.
  //
  // V2 design note (Fanny): "importante que se dispare una vez el endpoint
  // de apagado nos responda con éxito, para no generar furia / bloqueo".
  // The V1 flow gated the PATCH on the modal Confirm — V2 reverses it.
  const handleToggle = useCallback(
    async (entryPoint: tHandoffReasonEntryPoint) => {
      if (
        canSendOutboundMessages === false ||
        !currentWorkspace?.id ||
        !selectedChat?.id ||
        isSaving
      )
        return;

      const next = !enabled;
      const isHandoff = enabled && !next;

      // Step 1: fire the PATCH. Whatever the survey outcome, the bot
      // transitions immediately. performToggle handles its own
      // optimistic-update + rollback + error toast.
      const ok = await performToggle(next);
      if (!ok) return;

      // Only the deactivation path triggers a survey.
      if (!isHandoff) return;

      // Step 2: per-entry-point cooldown gate. The consumer routes which
      // surface this was: 'tomar_control' (pill, 4h cooldown, flat
      // optional survey) vs 'apagar_bot_chat' (AIResponsePreview switch,
      // 30min cooldown, mandatory nested survey).
      const now = new Date();
      if (
        !shouldAskForReason({ entryPoint, userId: currentWorkspace.id, now })
      ) {
        return;
      }

      const slbActivatedAt = getOrInitSlbActivatedAt({
        workspaceId: currentWorkspace.id,
        now,
      });
      const workspaceSlbAgeDays = workspaceSlbAgeDaysFrom(slbActivatedAt, now);

      // Step 3: open the modal. Cooldown is consumed inside the
      // Confirm / Skip / Close handlers — never at open-time — so a chat
      // switch that silently dismisses the modal doesn't burn the cap.
      const context: tPendingHandoffContext = {
        chatId: selectedChat.id,
        leadId: selectedChat.leadId ?? null,
        channelType: selectedChat.integration,
        workspaceSlbAgeDays,
        entryPoint,
      };
      setPendingHandoffContext(context);
      setIsReasonModalOpen(true);
      trackEvent(LEADAGENT_CHAT_EVENTS.HANDOFF_REASON_MODAL_SHOWN, {
        source: 'per_chat',
        entry_point: context.entryPoint,
        was_mandatory: context.entryPoint !== 'tomar_control',
        chat_id: context.chatId,
        lead_id: context.leadId,
        channel_type: context.channelType,
        workspace_slb_age_days: context.workspaceSlbAgeDays,
      });
    },
    [
      canSendOutboundMessages,
      currentWorkspace?.id,
      selectedChat?.id,
      selectedChat?.integration,
      selectedChat?.leadId,
      enabled,
      isSaving,
      performToggle,
      trackEvent,
    ],
  );

  const handleReasonConfirm = useCallback(
    async (result: tHandoffReasonModalResult) => {
      if (!pendingHandoffContext || !currentWorkspace?.id) return;
      recordReasonAsked({
        entryPoint: pendingHandoffContext.entryPoint,
        userId: currentWorkspace.id,
        now: new Date(),
      });
      trackEvent(LEADAGENT_CHAT_EVENTS.HANDOFF_REASON_SUBMITTED, {
        source: 'per_chat',
        entry_point: pendingHandoffContext.entryPoint,
        was_mandatory: pendingHandoffContext.entryPoint !== 'tomar_control',
        chat_id: pendingHandoffContext.chatId,
        lead_id: pendingHandoffContext.leadId,
        channel_type: pendingHandoffContext.channelType,
        workspace_slb_age_days: pendingHandoffContext.workspaceSlbAgeDays,
        reason_category: result.reason_category,
        reason_subcategory: result.reason_subcategory,
        reason_text: result.reason_text,
      });
      setIsReasonModalOpen(false);
      setPendingHandoffContext(null);
      // V2: PATCH already fired in handleToggle. Modal handlers are
      // now analytics-only.
    },
    [pendingHandoffContext, currentWorkspace?.id, trackEvent],
  );

  const handleReasonSkip = useCallback(() => {
    if (!pendingHandoffContext || !currentWorkspace?.id) return;
    recordReasonAsked({
      entryPoint: pendingHandoffContext.entryPoint,
      userId: currentWorkspace.id,
      now: new Date(),
    });
    trackEvent(LEADAGENT_CHAT_EVENTS.HANDOFF_REASON_SKIPPED, {
      source: 'per_chat',
      entry_point: pendingHandoffContext.entryPoint,
      was_mandatory: pendingHandoffContext.entryPoint !== 'tomar_control',
      chat_id: pendingHandoffContext.chatId,
      lead_id: pendingHandoffContext.leadId,
      channel_type: pendingHandoffContext.channelType,
      workspace_slb_age_days: pendingHandoffContext.workspaceSlbAgeDays,
      reason_category: 'skipped',
    });
    setIsReasonModalOpen(false);
    setPendingHandoffContext(null);
    // V2: PATCH already fired in handleToggle. Modal handlers are
    // now analytics-only.
  }, [pendingHandoffContext, currentWorkspace?.id, trackEvent]);

  const handleReasonClose = useCallback(
    (dismissReason: tHandoffReasonDismissReason) => {
      if (!pendingHandoffContext || !currentWorkspace?.id) return;
      recordReasonAsked({
        entryPoint: pendingHandoffContext.entryPoint,
        userId: currentWorkspace.id,
        now: new Date(),
      });
      trackEvent(LEADAGENT_CHAT_EVENTS.HANDOFF_REASON_DISMISSED, {
        source: 'per_chat',
        entry_point: pendingHandoffContext.entryPoint,
        was_mandatory: pendingHandoffContext.entryPoint !== 'tomar_control',
        dismiss_reason: dismissReason,
        chat_id: pendingHandoffContext.chatId,
        lead_id: pendingHandoffContext.leadId,
        channel_type: pendingHandoffContext.channelType,
        workspace_slb_age_days: pendingHandoffContext.workspaceSlbAgeDays,
      });
      setIsReasonModalOpen(false);
      setPendingHandoffContext(null);
      // V2: the PATCH already committed before the modal opened. The bot
      // is already off — dismissing the modal here just abandons the
      // optional feedback survey; the deactivation itself stands.
    },
    [pendingHandoffContext, currentWorkspace?.id, trackEvent],
  );

  // Stabilize the modal props so the consumer (HandoffReasonModal) doesn't
  // see a new reference on every hook render (e.g. each `isSaving` flip).
  // Without this, the modal's `handleConfirm` useCallback depends on a
  // shifting `props` reference and re-creates on every render — noisy and
  // matches the smart-config hook's existing pattern.
  const handoffReasonModalProps = useMemo(
    () => ({
      open: isReasonModalOpen,
      // Schema is keyed off the entryPoint the consumer routed through
      // handleToggle. Source is the PostHog flag (live-tunable) with
      // baked-in fallback. Fallback covers the defensive case where the
      // modal somehow opens without a pendingContext.
      schema:
        handoffSchemas[
          pendingHandoffContext?.entryPoint ?? FALLBACK_PER_CHAT_ENTRY_POINT
        ],
      onConfirm: handleReasonConfirm,
      onSkip: handleReasonSkip,
      onClose: handleReasonClose,
    }),
    [
      isReasonModalOpen,
      handoffSchemas,
      pendingHandoffContext?.entryPoint,
      handleReasonConfirm,
      handleReasonSkip,
      handleReasonClose,
    ],
  );

  return {
    enabled,
    isSaving,
    handleToggle,
    disabledReason,
    deactivationReason,
    isDeactivationReasonLoaded:
      !!selectedChat?.id && deactivationReasonLoadedChatId === selectedChat.id,
    handoffReasonModalProps,
  };
}
